import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, take } from 'rxjs/operators';

import { saveAs } from 'file-saver';
import { environment } from 'src/environments/environment';
import { Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FileTransferService {
  constructor(private httpClient: HttpClient) {}

  exportToXml(id: string) {
    return this.httpClient.post(`${environment.apiUrl}/products/${id}/exportXml`, '', { responseType: 'text', observe: 'response' }).pipe(
      take(1),
      map(response => {
        const contentDisposition = response.headers.get('content-disposition') || '';
        const matches = /filename=([^;]+)/gi.exec(contentDisposition);
        let fileName = '';
        if (matches[1] && matches[1] !== '.xml') {
          fileName = matches[1].replace(/["]+/g, '');
        } else {
          fileName = 'unnamed.xml';
        }
        return saveAs(new Blob([response.body], { type: 'text/xml; charset=utf-8' }), this.decodeFileName(fileName));
      }),
      catchError((response: HttpErrorResponse) => {
        return this.isBlobError(response) ? this.parseErrorBlob(response) : throwError(() => response);
      })
    );
  }

  exportToMagiCAD(id: string) {
    return this.httpClient.get(`${environment.apiUrl}/magicadplugin/project/${id}`, { responseType: 'blob', observe: 'response' }).pipe(
      take(1),
      map(response => {
        const contentDisposition = response.headers.get('content-disposition') || '';
        const matches = /filename=([^;]+)/gi.exec(contentDisposition);
        let fileName = '';
        if (matches[1] && matches[1] !== '.mchoodproject') {
          fileName = matches[1].replace(/["]+/g, '');
        } else {
          fileName = 'unnamed.mchoodproject';
        }
        return saveAs(new Blob([response.body]), this.decodeFileName(fileName));
      }),
      catchError((response: HttpErrorResponse) => {
        return this.isBlobError(response) ? this.parseErrorBlob(response) : throwError(() => response);
      })
    );
  }

  getReport(reportType: string, projectId: string, languageCode: string, extension: string) {
    const data = {
      ReportType: reportType,
      ProjectId: projectId,
      LanguageCode: languageCode
    };
    return this.httpClient.post(`${environment.apiUrl}/report/${extension}`, data, { responseType: 'blob', observe: 'response' }).pipe(
      take(1),
      map(response => {
        const contentDisposition = response.headers.get('content-disposition') || '';
        const matches = /filename=([^;]+)/gi.exec(contentDisposition);
        const fileName = matches[1] || `${reportType}.${extension}`;
        return saveAs(response.body, this.decodeFileName(fileName));
      }),
      catchError((response: HttpErrorResponse) => {
        return this.isBlobError(response) ? this.parseErrorBlob(response) : throwError(() => response);
      })
    );
  }

  exportAllDocuments(projectId: string, languageCode: string) {
    const data = {
      ProjectId: projectId,
      LanguageCode: languageCode
    };
    return this.httpClient.post(`${environment.apiUrl}/export/alldocuments`, data, { responseType: 'blob', observe: 'response' }).pipe(
      take(1),
      map(response => {
        const contentDisposition = response.headers.get('content-disposition') || '';
        const matches = /filename=([^;]+)/gi.exec(contentDisposition);
        let fileName = '';
        if (matches[1] && matches[1] !== '.zip') {
          fileName = matches[1].replace(/["]+/g, '');
        } else {
          fileName = `${projectId}_alldocuments.zip`;
        }
        return saveAs(response.body, this.decodeFileName(fileName));
      }),
      catchError((response: HttpErrorResponse) => {
        return this.isBlobError(response) ? this.parseErrorBlob(response) : throwError(() => response);
      })
    );
  }

  importFromXml(file: File, parentId: string | null) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`${environment.apiUrl}/products/${parentId}/importXml`, formData).pipe(
      take(1),
      // Id of the imported project in the response array
      map(response => {
        return response[0];
      })
    );
  }

  isBlobError(error: any) {
    return error instanceof HttpErrorResponse && error.error instanceof Blob && error.error.type === 'application/json';
  }

  parseErrorBlob(error: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();
    const obs = new Observable((observer: any) => {
      reader.onloadend = e => {
        observer.error(
          new HttpErrorResponse({
            ...error,
            error: JSON.parse(reader.result as string)
          })
        );
        observer.complete();
      };
    });
    reader.readAsText(error.error);
    return obs;
  }

  private decodeFileName(encodedFileName: string): string {
    return decodeURIComponent(encodedFileName).replace(/\+/g, ' ');
  }
}
